import { defineStore } from 'pinia'
import { ref, computed, shallowRef } from 'vue'
import type {
  PortInfoData,
  HostInfo,
  FocusRecordingData,
} from '@/device-ui'
import { A210cClient, A210C_USB_VID, A210C_USB_PID } from '@/services/a210c-client'
import { createSerialCoordinator } from '@/services/serial-coordinator'

type AnySerialPort = any

/** 自动重连兜底轮询间隔（与 wilwei 调试页一致） */
const AUTO_CONNECT_POLL_MS = 2500

export const useDeviceStore = defineStore('device', () => {
  // 用 shallowRef 避免 Vue 把 SerialPort / Client 内部深响应化
  const port = shallowRef<AnySerialPort | null>(null)
  const client = shallowRef<A210cClient | null>(null)
  const isConnected = ref(false)
  const portInfo = ref<PortInfoData | null>(null)
  const focusData = ref<FocusRecordingData | null>(null)
  const lastError = ref<string>('')
  /** 串口被其它标签页 / 桌面 App 接管（让出中，等焦点回来自动夺回） */
  const preempted = ref(false)

  /**
   * 用户手动断开：不再自动夺回，直到再次手动连接（或物理重插设备）。
   * 用 localStorage 持久化「手动断开」意图：刷新、关闭标签页后重开、
   * 乃至其它标签页都保持断开，不自动重连。再次「连接」或物理重插会清除该标记。
   */
  const USER_DISCONNECTED_KEY = 'a210c.userDisconnected'
  let userDisconnected = (() => {
    try {
      return localStorage.getItem(USER_DISCONNECTED_KEY) === '1'
    } catch {
      return false
    }
  })()
  function setUserDisconnected(v: boolean): void {
    userDisconnected = v
    try {
      if (v) localStorage.setItem(USER_DISCONNECTED_KEY, '1')
      else localStorage.removeItem(USER_DISCONNECTED_KEY)
    } catch {
      /* 隐私模式等禁用 storage：退化为内存语义 */
    }
  }
  /** 重入保护：避免并发 open 同一串口 */
  let connectInProgress = false
  let autoConnectPollTimer: ReturnType<typeof setInterval> | null = null

  /**
   * 串口操作互斥：连接 / 让出 / 断开必须串行。
   * 并发对同一 SerialPort getReader/getWriter 会把旧 client 覆盖成「孤儿」，
   * 其流锁永远无法释放 → port.close() 永远失败 → 端口变成谁也打不开的僵尸。
   */
  let opChain: Promise<unknown> = Promise.resolve()
  function withPortLock<T>(fn: () => Promise<T>): Promise<T> {
    const run = opChain.then(fn, fn)
    opChain = run.then(
      () => {},
      () => {},
    )
    return run
  }

  // hostInfo 从 portInfo 派生（口径与 ADM 桌面端一致）
  const hostInfo = computed<HostInfo>(() => {
    const p = portInfo.value
    if (!p) {
      return { pcModel: '--', hubPower: '--W', chargingPower: '--W', hostEmpty: true }
    }
    const ufp = p.ufpInfo as any
    const pdIn = p.pdInInfo as any
    const pdAdc = p.pdAdcInfo as any
    const pdInLinked = !!pdIn?.linked
    const fmtW2 = (mw: number) => (mw / 1000).toFixed(2) + 'W'
    // 与 ADM 对齐：PD-IN 接入时，Hub 功率为 PD ADC 实测功率 - UFP 实测功率；
    // 充电功率直接展示 UFP 实测功率，避免 web 和 App/设备端显示口径不一致。
    const hubPower = pdInLinked
      ? pdAdc?.powerMw > 0
        ? fmtW2(Math.max(0, pdAdc.powerMw - (ufp?.linked ? ufp.powerMw || 0 : 0)))
        : '--W'
      : ufp?.linked
        ? ufp.powerW || '--W'
        : '--W'
    const chargingPower = pdInLinked && ufp?.linked ? ufp.powerW || '--W' : '--W'
    return {
      pcModel: ufp?.deviceName || '--',
      hubPower,
      chargingPower,
      hostEmpty: !pdInLinked,
      // Popover 规格值（与 ADM maxPdIn / maxChargingPower 一致）
      pdInPower: pdInLinked ? pdIn?.powerW || '--' : '--',
      chargingPowerMax: pdInLinked ? ufp?.negPowerW || '--' : '--',
    }
  })

  /* --- 串口抢占协调（跟随焦点）：BroadcastChannel 协调多标签页 + WS 协调桌面 App --- */
  const coordinator = createSerialCoordinator({
    onYieldRequested: () => yieldToPeer(),
    onBecameActive: () => onBecameActive(),
    onBusy: () => {
      preempted.value = true
      stopAutoConnectPoll()
    },
  })

  /** 收到对端 release_port：关闭连接但保留端口授权，等焦点回来夺回 */
  async function yieldToPeer(): Promise<void> {
    stopAutoConnectPoll()
    const p = port.value
    const portStillOpen = !!(p && (p.readable || p.writable))
    if (!client.value && !isConnected.value && !portStillOpen) return
    preempted.value = true
    console.info('[serial-coord] 收到占用请求，让出串口')
    await withPortLock(async () => {
      const c = client.value
      client.value = null
      if (c) {
        await c.close().catch(() => {})
      }
      // 端口级兜底：client 关闭异常（或残留半开端口）时直接关端口对象，
      // 确保回执 port_released 时串口真的已释放
      if (p && (p.readable || p.writable)) {
        try {
          await p.close()
          console.info('[serial-coord] 端口级兜底关闭成功')
        } catch (e: any) {
          console.error('[serial-coord] 让出失败，端口仍被锁定:', e?.message || e)
        }
      }
    })
    isConnected.value = false
  }

  /** 本窗口变为活跃（focus / 重新可见 / 对端离开）→ 立即尝试夺回，不等兜底轮询。
   * preempted 不在此处清，由 startClient 成功后清：夺回期间（~1s）若提前清，
   * 全局导航 watch 会把仍在设备页的本 tab 误踢回首页再弹回，造成闪跳。 */
  function onBecameActive(): void {
    if (userDisconnected || isConnected.value || !hasSerialApi()) return
    console.info('[serial-coord] 页面变为前台，尝试接管 A210C 串口')
    startAutoConnectPoll()
    void tryReclaim()
  }

  function hasSerialApi(): boolean {
    // @ts-ignore — Web Serial API 类型
    return !!navigator.serial
  }

  function isForeground(): boolean {
    return typeof document !== 'undefined' && document.visibilityState === 'visible' && document.hasFocus()
  }

  /** 在已授权端口中找 A210C（新标签页无 port 引用时用） */
  async function findGrantedA210CPort(): Promise<AnySerialPort | null> {
    if (!hasSerialApi()) return null
    try {
      // @ts-ignore
      const ports: any[] = await navigator.serial.getPorts()
      for (const p of ports) {
        const info = p.getInfo?.() || {}
        if (info.usbVendorId === A210C_USB_VID && info.usbProductId === A210C_USB_PID) return p
      }
      // mac 上部分 Chrome 版本 getInfo() 不返回 VID/PID：已授权仅 1 个端口时宽松匹配
      if (ports.length === 1) return ports[0]
    } catch {
      /* getPorts 失败忽略 */
    }
    return null
  }

  /** 尝试夺回串口：先广播 release_port 让对端让出，再打开 */
  async function tryReclaim(): Promise<void> {
    if (!isForeground() || connectInProgress || isConnected.value || userDisconnected) return
    // 优先现查（设备重插后旧 SerialPort 引用失效），缓存引用兜底（mac 上 getInfo 缺 VID/PID 的场景）
    const p = (await findGrantedA210CPort()) ?? port.value
    if (!p) {
      return
    }
    connectInProgress = true
    try {
      console.info('[serial-coord] 已发现授权 A210C 串口，请求其它上下文让出')
      const rel = await coordinator.requestRelease()
      if (rel === 'busy') {
        // App 前台正在用串口 → 退避（切回本页或 App 关闭后再试）
        console.info('[serial-coord] App 前台占用串口，Web 退避')
        preempted.value = true
        stopAutoConnectPoll()
        return
      }
      await startClient(p)
      stopAutoConnectPoll()
    } catch (e: any) {
      // 打开失败（对端未完全释放 / 设备拔出）→ 保留状态，等兜底轮询或下次 focus 重试
      console.warn('[serial-coord] 自动接管失败，等待下次重试:', e?.message || e)
    } finally {
      connectInProgress = false
    }
  }

  function startAutoConnectPoll(): void {
    if (autoConnectPollTimer) return
    autoConnectPollTimer = setInterval(() => void tryReclaim(), AUTO_CONNECT_POLL_MS)
  }

  function stopAutoConnectPoll(): void {
    if (autoConnectPollTimer) {
      clearInterval(autoConnectPollTimer)
      autoConnectPollTimer = null
    }
  }

  /** 打开端口并启动客户端（手动连接与自动夺回共用） */
  async function startClient(p: AnySerialPort): Promise<void> {
    await withPortLock(async () => {
      if (client.value) {
        await client.value.close().catch(() => {})
        client.value = null
      }
      port.value = p
      const c = new A210cClient(p, {
        onPortInfo: (info) => {
          portInfo.value = info
        },
        onFocusData: (info) => {
          focusData.value = info
        },
        onError: (err) => {
          lastError.value = err.message
        },
        onDisconnect: () => {
          isConnected.value = false
        },
      })
      client.value = c
      try {
        console.info('[serial-coord] 打开 A210C 串口')
        await c.open()
        isConnected.value = true
        preempted.value = false
        lastError.value = ''
        console.info('[serial-coord] A210C 串口已连接')
      } catch (err: any) {
        lastError.value = err?.message || String(err)
        isConnected.value = false
        if (client.value === c) client.value = null
        await c.close().catch(() => {})
        throw err
      }
    })
  }

  /** 手动连接入口（Home 选好端口后调用） */
  async function setPortAndStart(p: AnySerialPort): Promise<void> {
    if (connectInProgress) throw new Error('设备正在连接，请稍后重试')
    connectInProgress = true
    setUserDisconnected(false)
    try {
      // 先通知其它标签页 / App 让出；App 前台占用则不强开（避免双端同时打开同一串口）
      const rel = await coordinator.requestRelease()
      if (rel === 'busy') {
        preempted.value = true
        throw new Error('桌面 App 正在使用设备，请关闭 App 或切回本页面后重试')
      }
      await startClient(p)
      stopAutoConnectPoll()
    } finally {
      connectInProgress = false
    }
  }

  /** 手动断开：本标签页停止自动夺回，直到再次手动连接 */
  async function disconnect(): Promise<void> {
    setUserDisconnected(true)
    stopAutoConnectPoll()
    await withPortLock(async () => {
      if (client.value) {
        await client.value.close().catch(() => {})
        client.value = null
      }
    })
    port.value = null
    portInfo.value = null
    focusData.value = null
    isConnected.value = false
    preempted.value = false
    lastError.value = ''
  }

  /** 设备物理拔出：清空连接状态（isConnected→false 由全局 watch 带回首页） */
  async function handlePhysicalDisconnect(): Promise<void> {
    stopAutoConnectPoll()
    await withPortLock(async () => {
      const c = client.value
      client.value = null
      if (c) await c.close().catch(() => {})
    })
    port.value = null
    portInfo.value = null
    focusData.value = null
    isConnected.value = false
    preempted.value = false
    lastError.value = ''
  }

  // 设备插入（重插）且本页处于前台 → 立即重连，不用等焦点切换
  if (hasSerialApi()) {
    // @ts-ignore
    navigator.serial.addEventListener?.('connect', () => {
      // 物理重插不解除「手动断开」意图：用户显式断开后，重插设备也不应自动重连，
      // 需再次手动点击连接（CODING #26517）。onBecameActive 内部已判 userDisconnected，
      // 手动断开时会直接 return，不会自动夺回。
      console.info('[serial-coord] 浏览器收到串口 connect 事件')
      if (isForeground()) onBecameActive()
    })
    // 设备拔出 → 清状态回首页（被抢占让出时 port 引用仍保留，拔出同样能感知）
    // @ts-ignore
    navigator.serial.addEventListener?.('disconnect', (e: any) => {
      const p = port.value
      if (!p && !isConnected.value && !preempted.value) return
      if (e?.target && p && e.target !== p) {
        const info = e.target.getInfo?.() || {}
        const isA210C = info.usbVendorId === A210C_USB_VID && info.usbProductId === A210C_USB_PID
        if (!isA210C && !isConnected.value && !preempted.value) return
      }
      console.info('[serial-coord] 浏览器收到串口 disconnect 事件，清空设备状态')
      void handlePhysicalDisconnect()
    })
  }

  // 页面加载即活跃时尝试接管（新标签页无缝接力：已授权过的端口直接夺回）。
  // 但若此前是「手动断开」（localStorage 持久化），刷新 / 关闭重开后均不自动重连。
  if (isForeground() && !userDisconnected) {
    startAutoConnectPoll()
    void tryReclaim()
  }

  return {
    port,
    client,
    isConnected,
    preempted,
    portInfo,
    hostInfo,
    focusData,
    lastError,
    setPortAndStart,
    disconnect,
  }
})
